import { OnlineService } from 'src/app/services/general/online.service';
import { UrlService } from 'src/app/services/general/url.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { LinkService } from 'src/app/services/general/link.service';
import { PopupService } from 'src/app/services/general/popup.service';
import { Credit } from 'src/app/interfaces/credit';
import { AuthService } from 'src/app/services/user/auth.service';
import { UserService } from 'src/app/services/user/user.service';

import { LinksParam } from 'src/app/interfaces/links';
import { LoginUrl } from 'src/app/interfaces/login';
import { ErrorService } from 'src/app/services/general/error.service';
import { Platform } from '@ionic/angular';

@Component({
  selector: 'app-links',
  templateUrl: './links.page.html',
  styleUrls: ['./links.page.scss'],
})
export class LinksPage implements OnInit, OnDestroy {

  /**
   * Parm
   */
  param: LinksParam;
  /**
   * Valid flag
   */
  valid: boolean;
  /**
   * Checking flag
   */
  checking: boolean;

  ready: boolean;

  /**
   * Login ready flag
   */
  private loginReady?: boolean;
  /**
   * Login URL
   */
  private loginUrl: LoginUrl;
  /**
   * Login Modal
   */
  private loginModal: any;
  /**
   * Login ready subscription
   */
  private loginReadySubscription: Subscription;

  /**
   * Constructor
   * @param route route
   */
  constructor(
    private platform: Platform,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private fns: AngularFireFunctions,
    private linkService: LinkService,
    private urlService: UrlService,
    private authService: AuthService,
    private onlineService: OnlineService,
    private popupService: PopupService,
    private errorService: ErrorService,
  ) { }

  /**
   * Initialize
   */
  ngOnInit() {
    this.watchRoute();
  }

  ngOnDestroy(): void {
    this.unwatchLoginReady();
  }

  /**
   * Watch route
   */
  watchRoute() {
    this.route.params.subscribe((param: Params) => {
      this.param = param as LinksParam;
      this.checkParam();
    });
  }

  /**
   * Before view enter
   */
  ionViewWillEnter() {
    this.ready = true;
    this.watchLoginReady();
  }

  /**
   * Before view leave
   */
  async ionViewWillLeave() {
    this.unwatchLoginReady();
    this.param = null;
    this.valid = false;
    this.checking = false;
    this.ready = false;

    if (this.loginModal) {
      await this.loginModal.dismiss();
      this.loginModal = null;
    }
  }

  /**
   * Watch login ready
   */
  async watchLoginReady() {
    if (!this.loginReadySubscription) {
      this.loginReadySubscription = this.authService.observableLoginReady.subscribe(async (loginReady: boolean) => {
        this.loginReady = loginReady;
        if (this.loginReady && this.valid && !this.checking) {
          this.checkAction();
        }
      });
    }
    
  }

  /**
   * Unwatch login ready
   */
  async unwatchLoginReady() {
    if (this.loginReadySubscription) {
      this.loginReadySubscription.unsubscribe();
      this.loginReadySubscription = null;
    }
  }

  /**
   * Check param
   */
  checkParam() {
    this.valid = false;
    let redirect = true;

    if (this.param?.type === 'login') {
      if (this.param?.data) {
        this.valid = true;
      }
    } else if (this.param?.type === 'gift') {
      if (this.param?.data) {
        this.valid = true;
      }
    } else if (this.param?.type === 'promo') {
      redirect = false;
      this.goProductAccountPage();
    } else if (this.param?.type === 'download') {
      this.valid = true;
    } else if (this.param?.type === 'guest') {
      if (this.param?.data && this.param?.data2) {
        this.valid = true;
      }
    } else if (this.param?.type === 'migrate') {
      if (this.param?.data && this.param?.data2) {
        this.valid = true;
      }
    }

    if (this.valid) {
      this.checkAction();
    } else if (redirect) {
      // error
      this.goMainPage();
    }
  }

  /**
   * Check action
   */
  checkAction() {
    if (this.valid && this.param?.type && this.loginReady) {
      if (!this.checking) {
        this.checking = true;
        if (this.param.type === 'gift') {
          this.validateGiftCreditId();
        } else if (this.param.type === 'login') {
          this.validateAccountLogin();
        } else if (this.param.type === 'migrate') {
          this.validateMigrateLink();
        }
      }
    }
  }

  /**
   * Validate gift credit ID
   */
  async validateGiftCreditId() {
    if (this.param.data && !this.param.data2 && !this.param.data3 && !this.param.data4) {
      this.validateGiftCardCreditCall(this.param.data);
    } else {
      this.presentErrorModal(this.translate.instant('LINKS.error.gift'));
    }
  }

  /**
   * Validate gift card credit call
   * @param creditId Credit ID
   */
  async validateGiftCardCreditCall(creditId: string) {
    if (creditId) {
      await this.fns.httpsCallable('validateGiftCardCreditCall')({ creditId })
      .toPromise().then(async (credit: Credit) => {
        if (credit) {
          this.goGiftCardPage(credit);
        } else {
          this.presentErrorModal(this.translate.instant('LINKS.error.gift'));
        }
      }).catch((err: any) => {
        this.errorService.logError(err);
        this.presentErrorModal(this.translate.instant('LINKS.error.gift'));
      });
    } else {
      this.presentErrorModal(this.translate.instant('LINKS.error.gift'));
    }
  }

  validateMigrateLink() {
    if (this.param.data?.length === 11 && this.param.data2?.length === 17 && !this.param?.data3 && !this.param?.data4) {
      this.goAccountMigratePage(this.param.data, this.param.data2);
    } else if (this.param.data?.length === 11 && this.param?.data3?.length === 23 && !this.param?.data4) {
      this.goAccountMigratePage(this.param.data, null, this.param.data3);
    } else {
      this.presentErrorModal(this.translate.instant('LINKS.error.migrate'));
    }
  }

  /**
   * Validate account login
   */
  validateAccountLogin() {
    if (this.param.data && !this.param.data2 && !this.param.data3 && !this.param.data4) {
      this.checkAccountLogin(this.param.data);
    } else {
      this.presentErrorModal(this.translate.instant('LINKS.error.login'));
    }
  }

  /**
   * Check account login
   * @param qrcode QR Code
   */
  async checkAccountLogin(qrcode: string) {
    try {
      const text = qrcode.replace('/links/login/', '');
      const decoded = decodeURI(text).replace(/%2F/g, '/');
      const loginUrl: LoginUrl = JSON.parse(decoded);
      if (loginUrl?.login?.weddingId && loginUrl?.login?.weddingPass) {
        this.loginUrl = loginUrl;
        this.goAccountLoginPreviewPage(this.loginUrl, this.param.type === 'migrate' ? true : false);
      } else {
        this.presentErrorModal(this.translate.instant('LINKS.error.login'));
      }
    } catch (err) {
      this.errorService.logError(err);
      this.presentErrorModal(this.translate.instant('LINKS.error.login'));
    }
  }

  /**
   * Go product account page
   */
  async goProductAccountPage() {
    const url = '/subscription/account';
    if (!this.urlService.checkUrl([url])) {
      const navigationExtras: NavigationExtras = {
        state: {
          productId: this.param?.data && this.param?.data !== 'null' ? this.param.data : '',
          voucherId: this.param?.data2 && this.param?.data2 !== 'null' ? this.param.data2 : '',
          referralCode: this.param?.data3?.length >= 6 && this.param?.data3?.length <= 8 && this.param?.data3 !== 'null' ?
                      this.param.data3 : '',
        }
      };
      this.router.navigate(['/subscription/account'], navigationExtras);
    }
  }

  /**
   * Go gift card page
   * @param credit creidt
   */
  goGiftCardPage(credit: Credit) {
    const url = '/gift-card';
    if (credit?.creditId && !this.urlService.checkUrl([url])) {
      const navigationExtras: NavigationExtras = {
        state: {
          credit
        }
      };
      this.router.navigate(['/gift-card'], navigationExtras);
    }
  }

  /**
   * Go account login preview page
   * @param loginUrl login URL
   * @param migrate migrate flag
   */
  goAccountLoginPreviewPage(loginUrl: LoginUrl, migrate?: boolean) {
    const url = '/accounts/preview-login';
    if (!this.urlService.checkUrl([url])) {
      const navigationExtras: NavigationExtras = {
        state: {
          loginUrl,
          migrate,
          loginMethod: 'url',
        }
      };
      this.router.navigate([ url ], navigationExtras);
    }
  }

  /**
   * Go main page
   */
  async goMainPage() {
    this.linkService.goMainPage();
  }

  /**
   * Go account login page
   * @param accountId Account ID
   */
  goAccountLoginPage(accountId: string) {
    const url = '/accounts/login';
    if (accountId && !this.urlService.checkUrl([url])) {
      const navigationExtras: NavigationExtras = {
        state: { accountId, new: true, loginMethod: 'url' }
      };
      this.router.navigate(['/accounts/login'], navigationExtras);
    }
  }

  goAccountMigratePage(customerId: string, v1AccountId: string, customerDb?: string) {
    const url = '/accounts/migrate';
    if (customerId && (v1AccountId || customerDb) && !this.urlService.checkUrl([url])) {
      const navigationExtras: NavigationExtras = {
        state: { customerId, v1AccountId, customerDb }
      };
      this.router.navigate([url], navigationExtras);
    }
  }

  /**
   * Present error modal
   * @param msg Msg
   * @param disableRedirect Disable redirect
   */
  async presentErrorModal(msg?: string, disableRedirect?: boolean) {
    if (!msg) {
      msg = '';
    }
    const modal =  await this.popupService.presentAlert(msg);
    modal.onWillDismiss().then(() => {
      if (!disableRedirect) {
        this.goMainPage();
      }
    });
  }
}
